import { useMemo } from 'react';
import { useChangeRequests, useProjectAmsIncidents } from '../services/api';
import { useEngineeringRequest } from '../services/engineeringRequests';

/**
 * Resolves an Engineering Request's mission down to its change request (and
 * any linked incident) — shared by the Engineering tab (EngineeringMissionView)
 * and the Execution tab (ExecutionsCrView) so both consume the exact same
 * resolution path for a request-driven mission.
 */
export function useEngineeringRequestMission(projectId: string, requestId: string | undefined) {
  const requestQ = useEngineeringRequest(projectId, requestId);
  const request = requestQ.data;
  const crId = request?.mission_reference;

  const { data: crData, isLoading: crLoading } = useChangeRequests(projectId);
  const changeRequests = crData?.change_requests ?? [];
  const { data: incidentsRaw } = useProjectAmsIncidents(projectId);
  const incidents = incidentsRaw ?? [];

  const activeCr = changeRequests.find((cr) => cr.id === crId);
  const linkedIncident = useMemo(() => {
    if (!activeCr) return null;
    const scopeIncidentId =
      typeof activeCr.scope?.incident_id === 'string' ? activeCr.scope.incident_id : undefined;
    if (scopeIncidentId) return incidents.find((i) => i.id === scopeIncidentId) ?? null;
    return incidents.find((i) => i.change_request_id === crId) ?? null;
  }, [activeCr, crId, incidents]);

  return {
    request,
    crId,
    changeRequests,
    activeCr,
    linkedIncident,
    isLoading: requestQ.isLoading || crLoading,
  };
}
